// Deterministic, rule-based statement parser. Works on plain text lines (from PDF.js or OCR).
import { round2 } from "./format";

const MON = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const MON_RE = "(jan|feb|mar|apr|may|jun|jul|aug|sept|sep|oct|nov|dec)[a-z]*\\.?";

const DATE_PATTERNS = [
  { re: /^(\d{4})-(\d{1,2})-(\d{1,2})\b/, map: (m) => [m[2], m[3], m[1]] },
  { re: /^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?(?![\d.,])/, map: (m) => [m[1], m[2], m[3]] },
  { re: new RegExp(`^${MON_RE}\\s+(\\d{1,2})(?:,?\\s+(\\d{4}))?\\b`, "i"), map: (m) => [MON[m[1].toLowerCase().slice(0, 3)], m[2], m[3]] },
  { re: new RegExp(`^(\\d{1,2})\\s+${MON_RE}(?:,?\\s+(\\d{4}))?\\b`, "i"), map: (m) => [MON[m[2].toLowerCase().slice(0, 3)], m[1], m[3]] },
];

const AMOUNT_RE = /(?:^|\s)(\(?[-−]?\s?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}\)?(?:\s?(?:CR|DR|-))?)(?=\s|$)/gi;

const BANKS = ["Chase", "Bank of America", "Wells Fargo", "Citibank", "Citi", "Capital One", "U.S. Bank", "US Bank", "PNC", "TD Bank", "Ally", "Discover", "American Express", "Navy Federal", "USAA", "Truist", "Fifth Third", "Regions", "Charles Schwab", "Fidelity", "HSBC", "Barclays", "Santander", "Citizens", "KeyBank", "Huntington", "BMO", "Synchrony", "SoFi", "Chime", "Marcus", "Goldman Sachs", "First National Bank", "Sample Community Bank"];

export function parseAmountToken(tok) {
  const t = tok.trim();
  const neg = /^\(|^[-−]|-$/.test(t) || /\)$/.test(t);
  const cr = /CR$/i.test(t);
  const dr = /DR$/i.test(t);
  const value = parseFloat(t.replace(/[^0-9.]/g, ""));
  return { value, neg: neg || dr, cr };
}

export function findAmounts(s) {
  const out = [];
  let m;
  AMOUNT_RE.lastIndex = 0;
  while ((m = AMOUNT_RE.exec(s))) out.push({ ...parseAmountToken(m[1]), index: m.index + m[0].indexOf(m[1]), raw: m[1] });
  return out;
}

function normYear(y, fallback) {
  if (!y) return fallback;
  const n = parseInt(y, 10);
  return n < 100 ? 2000 + n : n;
}

export function matchDate(line) {
  for (const p of DATE_PATTERNS) {
    const m = line.match(p.re);
    if (m) {
      const [mo, d, y] = p.map(m);
      const month = parseInt(mo, 10);
      const day = parseInt(d, 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return { month, day, year: y ? normYear(y) : null, length: m[0].length };
    }
  }
  return null;
}

function parseLooseDate(s) {
  if (!s) return null;
  const d = matchDate(s.trim());
  if (!d || !d.year) return null;
  return iso(d.year, d.month, d.day);
}

const DATE_TOKEN = new RegExp(`(${MON_RE}\\s+\\d{1,2}(,?\\s+\\d{4})?|\\d{1,2}[/-]\\d{1,2}([/-]\\d{2,4})?|\\d{4}-\\d{2}-\\d{2}|\\b\\d{4}\\b)`, "gi");

// A dated line with no amount that is only a date range or header text is not a transaction
export function isNonTransactionLine(desc = "") {
  const left = desc.replace(DATE_TOKEN, " ").replace(/\b(to|through|thru|and)\b/gi, " ").replace(/[\s\-–—,.:()/]+/g, "");
  return left.length === 0 || /(statement|period|page \d|balance|closing date|due date|opening date|billing cycle|as of)/i.test(desc);
}

const iso = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export function detectPeriod(text) {
  const D1 = `(${MON_RE}\\s+\\d{1,2},?\\s+\\d{4}|\\d{1,2}/\\d{1,2}/\\d{2,4}|\\d{4}-\\d{2}-\\d{2})`;
  const re = new RegExp(`${D1}\\s*(?:-|–|—|to|through|thru)\\s*${D1}`, "i");
  const m = text.match(re);
  if (m) {
    const start = parseLooseDate(m[1]);
    const end = parseLooseDate(m[3]);
    if (start && end) return { start, end };
  }
  const close = text.match(/(?:closing date|statement date|period ending|ending date)[:\s]+(\d{1,2}\/\d{1,2}\/\d{2,4}|[A-Za-z]+\.? \d{1,2},? \d{4})/i);
  if (close) {
    const end = parseLooseDate(close[1]);
    if (end) return { start: null, end };
  }
  return null;
}

export function detectBank(text) {
  const head = text.slice(0, 3000).toLowerCase();
  return BANKS.find((b) => new RegExp(`(^|[^a-z])${b.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z]|$)`).test(head)) || null;
}

export function detectAccountType(text) {
  const t = text.slice(0, 5000).toLowerCase();
  if (/(minimum payment due|credit limit|new balance|payment due date|cash advance)/.test(t)) return "credit";
  if (/savings/.test(t) && !/checking/.test(t)) return "savings";
  return "checking";
}

const IN_WORDS = /(deposit|payroll|direct dep|salary|refund|interest paid|interest earned|credit\b|reversal|ssa treas|transfer from|zelle from|cash back|returned|rebate|dividend|tax ref)/i;
const CARD_IN_WORDS = /(payment|thank you|credit\b|refund|return|reversal|adjustment)/i;
const SKIP = /(page \d+ of|continued on|account number|customer service|^total\b|daily balance|balance summary|transaction detail|date\s+description|^date\b)/i;

function sectionOf(lower) {
  if (/^(transactions|new charges|purchases( and adjustments)?|card transactions)$/.test(lower.trim())) return "out";
  if (/(deposits|additions|credits|payments and (other )?credits|payments, credits)/.test(lower) && !/withdrawals and/.test(lower)) return "in";
  if (/(withdrawals|debits|checks paid|purchases|fees charged|charges|electronic payments|card activity)/.test(lower)) return "out";
  return null;
}

export function parseStatementText(rawText, opts = {}) {
  const text = rawText.replace(/\r/g, "");
  const lines = text.split("\n").map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  const period = detectPeriod(text);
  const bank = detectBank(text);
  const accountType = opts.accountType || detectAccountType(text);
  const isCard = accountType === "credit";
  const yearMatch = text.match(/\b(20\d{2})\b/);
  const endYear = period?.end ? parseInt(period.end.slice(0, 4), 10) : yearMatch ? parseInt(yearMatch[1], 10) : null;
  const endMonth = period?.end ? parseInt(period.end.slice(5, 7), 10) : 12;
  const last4 = (text.match(/(?:account|card)(?: number| ending| #)?[^\d\n]{0,20}(?:x+|\*+|ending in\s*)(\d{4})/i) || [])[1] || null;
  const warnings = [];
  if (!endYear) warnings.push("Statement year not found; dates use the current year. Please verify.");

  let opening = null;
  let closing = null;
  const totals = {};
  let section = null;
  let prevBalance = null;
  const txs = [];
  let cur = null;

  for (const line of lines) {
    const lower = line.toLowerCase();
    const amts = findAmounts(line);
    const date = matchDate(line);

    if (/(beginning|opening|previous|starting) balance/.test(lower) && amts.length && !date) {
      opening = amts[amts.length - 1].value * (amts[amts.length - 1].neg ? -1 : 1);
      prevBalance = opening;
      continue;
    }
    if (/(ending|closing|new) balance/.test(lower) && amts.length && !(date && amts.length > 1)) {
      closing = amts[amts.length - 1].value * (amts[amts.length - 1].neg ? -1 : 1);
      cur = null;
      continue;
    }
    const tot = lower.match(/^total (deposits|credits|additions|payments|withdrawals|debits|checks|purchases|fees|electronic)/);
    if (tot && amts.length) {
      totals[/(deposits|credits|additions|payments)/.test(tot[1]) && !isCard ? "in" : /(payments|credits)/.test(tot[1]) ? "in" : "out"] = amts[amts.length - 1].value;
      cur = null;
      continue;
    }
    if (!date) {
      const sec = sectionOf(lower);
      if (sec && amts.length === 0 && line.length < 70) {
        section = sec;
        cur = null;
        continue;
      }
      if (SKIP.test(lower)) {
        cur = null;
        continue;
      }
      if (cur && amts.length === 0 && line.length < 80 && cur.extra < 2 && !/^[A-Z\s]{25,}$/.test(line)) {
        cur.description = `${cur.description} ${line}`.trim();
        cur.extra += 1;
      } else if (cur && cur.pendingAmount && amts.length) {
        cur.amts = amts;
        cur.pendingAmount = false;
        cur.description = `${cur.description} ${line.slice(0, amts[0].index)}`.trim();
      }
      continue;
    }

    // Transaction line
    let rest = line.slice(date.length).trim();
    const second = matchDate(rest);
    if (second) rest = rest.slice(second.length).trim();
    const ra = findAmounts(rest);
    const description = (ra.length ? rest.slice(0, ra[0].index) : rest).replace(/\$\s*$/, "").trim();
    let year = date.year || endYear || new Date().getFullYear();
    if (!date.year && endYear && date.month > endMonth) year = endYear - 1;
    cur = { date: iso(year, date.month, date.day), description, amts: ra, section, pendingAmount: ra.length === 0, extra: 0, yearGuessed: !date.year && !endYear };
    txs.push(cur);
  }

  const transactions = [];
  for (const t of txs) {
    const flags = [];
    if (t.yearGuessed) flags.push("Year assumed");
    if (!t.amts.length) {
      if (isNonTransactionLine(t.description)) continue;
      transactions.push({ date: t.date, description: t.description || "(no description)", amount: 0, balance: null, flagged: true, flagReason: "Amount not found — please enter it" });
      continue;
    }
    let amt = t.amts[0];
    let balance = null;
    if (t.amts.length >= 2 && !isCard) {
      const b = t.amts[t.amts.length - 1];
      balance = b.value * (b.neg ? -1 : 1);
      if (t.amts.length >= 3) amt = t.amts.find((a, i) => i < t.amts.length - 1 && a.value > 0) || amt;
    }
    let sign = null;
    if (balance !== null && prevBalance !== null) {
      const diff = round2(balance - prevBalance);
      if (Math.abs(Math.abs(diff) - amt.value) < 0.011) sign = diff >= 0 ? 1 : -1;
      else flags.push("Running balance does not match amount");
    }
    if (balance !== null) prevBalance = balance;
    if (sign === null) {
      if (isCard) {
        if (amt.neg || amt.cr) sign = 1;
        else if (t.section === "in") sign = 1;
        else if (t.section === "out") sign = -1;
        else sign = CARD_IN_WORDS.test(t.description) && /payment|credit|refund|return/i.test(t.description) ? 1 : -1;
      } else if (amt.cr) sign = 1;
      else if (amt.neg) sign = -1;
      else if (t.section) sign = t.section === "in" ? 1 : -1;
      else if (IN_WORDS.test(t.description)) {
        sign = 1;
        flags.push("Direction inferred from description");
      } else {
        sign = -1;
        flags.push("Direction assumed (withdrawal)");
      }
    }
    if (!t.description || t.description.length < 3) flags.push("Description incomplete");
    transactions.push({
      date: t.date,
      description: t.description || "(no description)",
      amount: round2(sign * amt.value),
      balance,
      flagged: flags.length > 0,
      flagReason: flags.join("; "),
    });
  }

  // Reconciliation
  let reconciliation = null;
  const sum = round2(transactions.reduce((s, t) => s + t.amount, 0));
  if (opening !== null && closing !== null) {
    const expected = isCard ? round2(opening - sum) : round2(opening + sum);
    reconciliation = { opening, closing, computed: expected, difference: round2(closing - expected), ok: Math.abs(closing - expected) < 0.02 };
  } else if (totals.in !== undefined || totals.out !== undefined) {
    const inSum = round2(transactions.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0));
    const outSum = round2(-transactions.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0));
    reconciliation = { totals, inSum, outSum, ok: (totals.in === undefined || Math.abs(totals.in - inSum) < 0.02) && (totals.out === undefined || Math.abs(totals.out - outSum) < 0.02) };
  }
  if (!transactions.length) warnings.push("No transactions recognized. The layout may be unusual — try CSV import or add transactions manually.");
  if (reconciliation && !reconciliation.ok) warnings.push("Extracted totals do not match the statement. Some transactions may be missing or misread — review flagged items.");

  return { bank, accountType, last4, period, openingBalance: opening, closingBalance: closing, transactions, reconciliation, warnings };
}

// CSV import: rows are arrays of objects keyed by header
export function parseCsvRows(rows) {
  if (!rows.length) return { transactions: [], warnings: ["CSV file is empty"] };
  const headers = Object.keys(rows[0]);
  const find = (re) => headers.find((h) => re.test(h));
  const hDate = find(/date/i);
  const hDesc = find(/desc|memo|payee|merchant|name|detail/i);
  const hAmt = find(/^amount$|amount/i);
  const hDebit = find(/debit|withdraw|out/i);
  const hCredit = find(/credit|deposit|in\b/i);
  const hBal = find(/balance/i);
  const warnings = [];
  if (!hDate || (!hAmt && !hDebit && !hCredit)) return { transactions: [], warnings: ["Could not find Date and Amount columns in the CSV."] };
  const num = (v) => {
    if (v === undefined || v === null || String(v).trim() === "") return null;
    const p = parseAmountToken(String(v));
    return Number.isNaN(p.value) ? null : p.value * (p.neg ? -1 : 1);
  };
  const transactions = rows.map((r) => {
    const d = String(r[hDate] || "").trim();
    const dm = matchDate(d);
    const date = dm ? iso(dm.year || new Date().getFullYear(), dm.month, dm.day) : null;
    let amount = hAmt ? num(r[hAmt]) : null;
    if (amount === null) {
      const de = num(r[hDebit]);
      const cr = num(r[hCredit]);
      amount = cr ? Math.abs(cr) : de ? -Math.abs(de) : null;
    }
    const flags = [];
    if (!date) flags.push("Date not recognized");
    if (amount === null) flags.push("Amount missing");
    if (dm && !dm.year) flags.push("Year assumed");
    return { date: date || "", description: String(r[hDesc] || "").trim() || "(no description)", amount: round2(amount || 0), balance: hBal ? num(r[hBal]) : null, flagged: flags.length > 0, flagReason: flags.join("; ") };
  });
  return { transactions, warnings };
}
